'use client';

import Image from 'next/image';
import React from 'react';

interface AppImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  priority?: boolean;
  quality?: number;
  fill?: boolean;
  sizes?: string;
  onClick?: () => void;
  fallbackSrc?: string;
  [key: string]: any;
}

export default function AppImage({
  src,
  alt,
  width,
  height,
  className = '',
  priority = false,
  quality = 75,
  fill = false,
  sizes,
  onClick,
  fallbackSrc = 'https://cialfor.com/assets/no_image.png',
  ...props
}: AppImageProps) {
  const isExternal =
    src.startsWith('http://') || src.startsWith('https://');

  const handleError = (
    e: React.SyntheticEvent<HTMLImageElement>
  ) => {
    const target = e.currentTarget;
    if (fallbackSrc && target.src !== fallbackSrc) {
      target.src = fallbackSrc;
    }
  };

  /* ============================
     External images → <img>
     ============================ */
  if (isExternal) {
    if (fill) {
      return (
        <div data-component-id="src/components/ui/AppImage.tsx:53:8" data-component-path="src/components/ui/AppImage.tsx" data-component-line="53" data-component-file="AppImage.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
          className={`relative ${className}`}
          style={{ width: width || '100%', height: height || '100%' }}
        >
          <img data-component-id="src/components/ui/AppImage.tsx:57:10" data-component-path="src/components/ui/AppImage.tsx" data-component-line="57" data-component-file="AppImage.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22src%22%3A%22%5Bvar%3Asrc%5D%22%2C%22alt%22%3A%22%5Bvar%3Aalt%5D%22%2C%22className%22%3A%22absolute%20inset-0%20w-full%20h-full%20object-cover%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
            src={src}
            alt={alt}
            className="absolute inset-0 w-full h-full object-cover"
            onError={handleError}
            onClick={onClick}
            loading={priority ? 'eager' : 'lazy'}
            {...props}
          />
        </div>
      );
    }

    return (
      <img data-component-id="src/components/ui/AppImage.tsx:71:6" data-component-path="src/components/ui/AppImage.tsx" data-component-line="71" data-component-file="AppImage.tsx" data-component-name="img" data-component-content="%7B%22elementName%22%3A%22img%22%2C%22src%22%3A%22%5Bvar%3Asrc%5D%22%2C%22alt%22%3A%22%5Bvar%3Aalt%5D%22%2C%22className%22%3A%22%5Bvar%3AclassName%5D%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
        src={src}
        alt={alt}
        width={width}
        height={height}
        className={className}
        onError={handleError}
        onClick={onClick}
        loading={priority ? 'eager' : 'lazy'}
        {...props}
      />
    );
  }

  /* ============================
     Local images → next/image
     ============================ */
  if (fill) {
    return (
      <div data-component-id="src/components/ui/AppImage.tsx:90:6" data-component-path="src/components/ui/AppImage.tsx" data-component-line="90" data-component-file="AppImage.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`relative ${className}`}>
        <Image data-component-id="src/components/ui/AppImage.tsx:91:8" data-component-path="src/components/ui/AppImage.tsx" data-component-line="91" data-component-file="AppImage.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22src%22%3A%22%5Bvar%3Asrc%5D%22%2C%22alt%22%3A%22%5Bvar%3Aalt%5D%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
          src={src}
          alt={alt}
          fill
          sizes={sizes || '100vw'}
          priority={priority}
          quality={quality}
          unoptimized
          onError={handleError}
          {...props}
        />
      </div>
    );
  }

  return (
    <Image data-component-id="src/components/ui/AppImage.tsx:107:4" data-component-path="src/components/ui/AppImage.tsx" data-component-line="107" data-component-file="AppImage.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22src%22%3A%22%5Bvar%3Asrc%5D%22%2C%22alt%22%3A%22%5Bvar%3Aalt%5D%22%2C%22className%22%3A%22%5Bvar%3AclassName%5D%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
      src={src}
      alt={alt}
      width={width || 400}
      height={height || 300}
      className={className}
      priority={priority}
      quality={quality}
      unoptimized
      onError={handleError}
      {...props}
    />
  );
}
